import { useEffect } from 'react';

import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import {
  BaseType,
  geoMercator,
  geoPath,
  scaleQuantize,
  select,
  Selection,
} from 'd3';
import * as topojson from 'topojson-client';

import { FeatureShape } from '../../../types';
import ukDnoTopojson from './ukDnoTopojson.json';

type MapProps = {
  locations?: MapLocation[];
  loading?: boolean;
};

export type MapLocation = {
  latitude: number;
  longitude: number;
  power_kw: number;
};

const viewboxWidth = 500;
const viewboxHeight = 700;

const projection = geoMercator()
  .center([-2.3252278, 55.013176])
  .translate([viewboxWidth / 2 + 40, viewboxHeight / 2])
  .scale(2400);

function configureSVG(svg: Selection<BaseType, unknown, HTMLElement, any>) {
  svg
    .attr('viewBox', `0 0 ${viewboxWidth} ${viewboxHeight}`)
    .attr('preserveAspectRatio', 'xMinYMin');

  // There's a more elegant D3-ish way to do this, but for now remove the existing groups before regenerating the svg
  svg.selectAll('g').remove();
  svg.selectAll('text').remove();
}

// @ts-expect-error
const ukDnoRegions = topojson.feature(
  ukDnoTopojson as any,
  ukDnoTopojson.objects.dno_areas as any,
) as {
  type: 'FeatureCollection';
  features: FeatureShape[];
};

const pathGenerator = geoPath().projection(projection);

function plotMap(loading: boolean) {
  const mapSvg = select('div #svg-content svg#map-layer');
  configureSVG(mapSvg);

  mapSvg
    .append('g')
    .attr('id', 'dno-areas')
    .append('path')
    .attr(
      'class',
      `${loading ? 'fill-[#4A386E]' : 'fill-purple-90 stroke-purple-70'}`,
    )
    .datum(ukDnoRegions)
    .attr('d', pathGenerator);
}

function plotPins(locations: MapLocation[]) {
  const pinsSvg = select('div #svg-content svg#pins-layer');
  configureSVG(pinsSvg);

  const points = locations
    .map(({ latitude, longitude, power_kw }) => ({
      coords: projection([longitude, latitude]),
      power_kw: power_kw,
    }))
    // Interested in higher power values, so we want to plot them on top of lower values
    .sort((a, b) => a.power_kw - b.power_kw);

  const pointsSelection = pinsSvg
    .selectAll('asset-location-pins')
    .data(points)
    .enter();

  pointsSelection
    .append('g')
    .attr('transform', (d) =>
      d.coords ? `translate(${d.coords[0]},${d.coords[1]}) scale(0.005)` : '',
    )
    .append('path')
    .attr('class', 'stroke-[20px] -translate-x-[40%] -translate-y-[65%]')
    .attr('style', ({ power_kw }) => {
      const value = colorScale(
        power_kw ? Math.abs(Number(power_kw)) : 0,
      ) as unknown as string;
      return `fill: ${value}; background: ${value}`;
    })
    .attr('d', faLocationDot.icon[4] as any);
}

const colorScale = scaleQuantize()
  .domain([0, 5])
  // @ts-ignore
  .range(['#8e43f0', '#ae73fd', '#c091fd', '#d2b0fe', '#e4d0fe']);

export function Map({ locations, loading }: MapProps) {
  useEffect(() => {
    plotMap(loading ?? false);
    plotPins(locations ?? []);
  }, [loading, locations]);

  const scaleThresholds = [colorScale.domain()[0], ...colorScale.thresholds()];

  return (
    <div
      id="svg-content"
      className="relative m-4 flex min-h-[30rem] items-center justify-center"
    >
      <svg className="absolute h-full w-full" id="map-layer" />
      {!loading && (
        <img
          className="absolute h-full w-full"
          src={'/infrastructure.svg'}
          alt="a trace of the core infrastructure of the UK electricity grid"
        />
      )}
      <svg className="absolute h-full w-full" id="pins-layer" />
      <div className="absolute bottom-0 right-[50%]">
        <div className="flex">
          {scaleThresholds.map((threshold, index, arr) => (
            <div>
              <div
                key={index}
                className="h-4 w-10"
                style={{ background: colorScale(threshold) }}
              ></div>
              <span className="text-xs">{`${threshold}${
                index === arr.length - 1 ? '+' : ''
              } kW`}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
